/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';
import { getPermissionLabels, getCategoriesOrder } from '../constants/permissions-matrix';
import type { PermissionCategory, PermissionEntry } from '../constants/permissions-matrix';

/**
 * Builds the role-permission matrix from the raw API response, grouping
 * permissions by category and flagging which roles hold each permission.
 *
 * @param apiResponse - Per-role lists of granted permission keys.
 * @param t - Translation function for permission labels.
 * @returns The ordered permission categories with per-role grants.
 */
export function buildPermissionMatrix(
  apiResponse: Record<string, string[]>,
  t: TFunction,
): PermissionCategory[] {
  const adminPerms = new Set(apiResponse.admin ?? []);
  const operationalPerms = new Set(apiResponse.operational ?? []);
  const brokerPerms = new Set(apiResponse.broker ?? []);

  const grouped: Record<string, PermissionEntry[]> = {};

  for (const meta of getPermissionLabels(t)) {
    if (!grouped[meta.category]) {
      grouped[meta.category] = [];
    }
    grouped[meta.category].push({
      key: meta.key,
      label: meta.label,
      admin: adminPerms.has(meta.key),
      operational: operationalPerms.has(meta.key),
      broker: brokerPerms.has(meta.key),
    });
  }

  return getCategoriesOrder(t)
    .filter((cat) => grouped[cat]?.length > 0)
    .map((cat) => ({ category: cat, permissions: grouped[cat] }));
}
